import { useState, useRef, useEffect, useCallback } from "react";
import "../../styles/ManageTemplate.css";

const API_BASE =  process.env.REACT_APP_API_BASE_URL;

async function apiFetch(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers:
      options.body instanceof FormData
        ? undefined
        : { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });

  let data = null;
  try {
    data = await res.json();
  } catch (e) {
    // No JSON body
  }

  if (!res.ok) {
    const message =
      (data && (data.message || JSON.stringify(data))) ||
      `Request failed with status ${res.status}`;
    throw new Error(message);
  }

  return data;
}

const TAB_TO_TEMPLATE_TYPE = {
  "REPORT FORMAT": "REPORT_FORMAT",
  SHORTCUT: "SHORTCUT",
};

function normalizeTemplate(t) {
  const plainContent = (t.content || "").replace(/<[^>]+>/g, "").slice(0, 60);
  return {
    id: t.id,
    description: t.description || "",
    shortcut: t.shortcut || "",
    content: t.content || "",
    contentPreview: plainContent,
    bodyPart: t.body_part || "",
    gender: t.gender || "",
    modality: t.modality || "",
    type: t.template_type || "",
  };
}

const TABS = ["ALL", "REPORT FORMAT", "SHORTCUT"];
const BODY_PARTS = ["ALL", "3D SCAN", "AB DOMEN", "ANGIO STUDY", "ANGIOGRAPHY PER PART","ANKLE","ANKLE JOINT", "ARM", "BOTH BREAST","BOTHLOWER LIMBS","BRACHIAL PLEXUS ","BRAIN","BRAINSELLA","BRAINWITHORBIT","BREAST","CERVICAL SPINE","CHEST","CLAVICLE","COCCYX","DORSALSPINE","DORSO LUMBAR SPINE","DORSOLUMBE SPINE","EAR","ELBOW","ELBOW JOINT","EXTRA REGION","EXTRMITIES","FACE","FACE & NECK","FEMUR","FISTULOGRAM","FOOT","FOREARM","HAND","HIP","HIP JOINT","HU MARUS","IUV","JOINT",
  "KNEE","KNEE JOINT","KUB","LEG","LOWER ABDOMEN","LUMBER SPINE","LUMBOSACRAL SPINE","MAMMOGRAHY","MASTOID","MAXILLA","MRCP","NASOPHARYNX","NECK","NOSE","OPG","ORBIT","OTHER BODYPART","PELVIS","PERINEUM","PNS","SACROCOCCYX","SCAPULA","SCREENING","SHOULDER","SHOULDER JOINT","SI JOINT","SKULL","SPECIAL STUDY","TEMPORALBONE","THORAX","TOUNGE","UPPER ABDOMEN","WRIST","WRIST JOINT"];
const GENDERS = ["ALL", "Male", "Female", "UNISEX", "OTHER"];
const MODALITIES = ["ALL", "CT","MR","SR","PR","ECG","DX","CR","OT","PT","NM","US","MG","XA","IR"];
const TYPES = ["ALL", "PERSONAL", "GROUP"];

// ---- Small UI Elements --------------------------------------------
function SelectField({ label, value, options, onChange }) {
  return (
    <div className="mt-select-wrapper">
      <span className="mt-select-label">{label}</span>
      <select className="mt-select" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}

function TypeBadge({ type }) {
  if (!type) return null;
  return <span className={`mt-type-badge mt-badge-${type.toLowerCase()}`}>{type}</span>;
}

function RowActions({ onEdit, onDelete }) {
  return (
    <div className="mt-cell mt-cell-actions">
      <button className="mt-icon-btn mt-edit-btn" title="Edit" onClick={onEdit}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      </button>
      <button className="mt-icon-btn mt-delete-btn" title="Delete" onClick={onDelete}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6l-1 14H6L5 6" />
          <path d="M10 11v6" />
          <path d="M14 11v6" />
          <path d="M9 6V4h6v2" />
        </svg>
      </button>
    </div>
  );
}

function ToolbarBtn({ label, onClick, title }) {
  return (
    <button type="button" className="mt-tb-btn" title={title || label} onMouseDown={(e) => e.preventDefault()} onClick={onClick}>
      {label}
    </button>
  );
}

// ---- Layout Adjusted Text Editor ------------------------------------
function RichTextEditor({ editorRef }) {
  const exec = (command, value = null) => {
    document.exec(command, false, value);
    editorRef.current?.focus();
  };

  return (
    <div className="mt-editor-frame">
      <div className="mt-editor-toolbar">
        <ToolbarBtn label="B" title="Bold" onClick={() => exec("bold")} />
        <ToolbarBtn label="I" title="Italic" onClick={() => exec("italic")} />
        <ToolbarBtn label="U" title="Underline" onClick={() => exec("underline")} />
        <ToolbarBtn label="X₂" title="Subscript" onClick={() => exec("subscript")} />
        <ToolbarBtn label="X²" title="Superscript" onClick={() => exec("superscript")} />
        <ToolbarBtn label="Tx" title="Clear formatting" onClick={() => exec("removeFormat")} />
        <span className="mt-tb-sep" />
        <ToolbarBtn label="↺" title="Undo" onClick={() => exec("undo")} />
        <ToolbarBtn label="↻" title="Redo" onClick={() => exec("redo")} />
        <ToolbarBtn label="1." title="Numbered list" onClick={() => exec("insertOrderedList")} />
        <ToolbarBtn label="•" title="Bullet list" onClick={() => exec("insertUnorderedList")} />
        <ToolbarBtn label="⇥" title="Increase indent" onClick={() => exec("indent")} />
        <ToolbarBtn label="⇤" title="Decrease indent" onClick={() => exec("outdent")} />
        <ToolbarBtn label="❝" title="Blockquote" onClick={() => exec("formatBlock", "blockquote")} />
        <span className="mt-tb-sep" />
        <ToolbarBtn label="⯇" title="Align left" onClick={() => exec("justifyLeft")} />
        <ToolbarBtn label="≡" title="Align center" onClick={() => exec("justifyCenter")} />
        <ToolbarBtn label="⯈" title="Align right" onClick={() => exec("justifyRight")} />
        <ToolbarBtn label="☰" title="Justify" onClick={() => exec("justifyFull")} />
        <span className="mt-tb-sep" />
        <ToolbarBtn label="▦" title="Insert table" onClick={() => exec("insertHTML", "<table border='1' style='width:100%'><tr><td>&nbsp;</td><td>&nbsp;</td></tr><tr><td>&nbsp;</td><td>&nbsp;</td></tr></table>")} />
        <ToolbarBtn label="Ω" title="Special character" onClick={() => exec("insertText", "Ω")} />

        <select className="mt-tb-select" defaultValue="" onChange={(e) => exec("formatBlock", e.target.value)}>
          <option value="" disabled>Format</option>
          <option value="p">Normal</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
        </select>
        <select className="mt-tb-select" defaultValue="" onChange={(e) => exec("fontName", e.target.value)}>
          <option value="" disabled>Font</option>
          <option value="Arial">Arial</option>
          <option value="Georgia">Georgia</option>
          <option value="Times New Roman">Times New Roman</option>
          <option value="Courier New">Courier New</option>
        </select>
        <select className="mt-tb-select" defaultValue="" onChange={(e) => exec("fontSize", e.target.value)}>
          <option value="" disabled>Size</option>
          <option value="2">Small</option>
          <option value="3">Normal</option>
          <option value="5">Large</option>
          <option value="7">Huge</option>
        </select>
        <select className="mt-tb-select" defaultValue="" onChange={(e) => { if(editorRef.current) editorRef.current.style.lineHeight = e.target.value }}>
          <option value="" disabled>Line Height</option>
          <option value="1">1</option>
          <option value="1.5">1.5</option>
          <option value="2">2</option>
        </select>
        <ToolbarBtn label="⛶" title="Fullscreen" onClick={() => editorRef.current?.requestFullscreen?.()} />
      </div>

      <div className="mt-editor-page-wrap">
        <div ref={editorRef} className="mt-editor-page" contentEditable suppressContentEditableWarning />
      </div>
    </div>
  );
}

// ---- Create Views -----------------------------------------------------
function CreateShortcutView({ onClose, onCreate, submitting, error }) {
  const editorRef = useRef(null);
  const [shortcut, setShortcut] = useState("");

  const handleCreate = () => {
    if (!shortcut.trim()) return;
    const content = editorRef.current?.innerHTML || "";
    onCreate?.({ shortcut, content });
  };

  return (
    <div className="mt-container mt-creation-layout">
      <div className="mt-view-header">
        <h3 className="mt-cs-title">Create Shortcut</h3>
        <button className="mt-close-top-btn" onClick={onClose}>&times;</button>
      </div>

      <div className="mt-form-row">
        <input
          type="text"
          className="mt-cs-shortcut-input"
          placeholder="Shortcut Code *"
          value={shortcut}
          onChange={(e) => setShortcut(e.target.value)}
        />
      </div>

      <RichTextEditor editorRef={editorRef} />

      {error && <div className="mt-error-text">{error}</div>}

      <div className="mt-cs-footer">
        <button className="mt-cs-close-btn" onClick={onClose} disabled={submitting}>CANCEL</button>
        <button className="mt-cs-create-btn" onClick={handleCreate} disabled={submitting}>
          {submitting ? "CREATING..." : "CREATE SHORTCUT"}
        </button>
      </div>
    </div>
  );
}

function CreateReportFormatView({ onClose, onCreate, submitting, error }) {
  const editorRef = useRef(null);
  const [formatName, setFormatName] = useState("");
  const [modalityTag, setModalityTag] = useState("");
  const [bodyPartTag, setBodyPartTag] = useState("");
  const [genderTag, setGenderTag] = useState("");

  const handleCreate = () => {
    if (!formatName.trim()) return;
    const content = editorRef.current?.innerHTML || "";
    onCreate?.({ formatName, modalityTag, bodyPartTag, genderTag, content });
  };

  return (
    <div className="mt-container mt-creation-layout">
      <div className="mt-view-header">
        <h3 className="mt-cs-title">Create Report Format</h3>
        <button className="mt-close-top-btn" onClick={onClose}>&times;</button>
      </div>

      <div className="mt-crf-field-grid">
        <input
          type="text"
          className="mt-crf-input"
          placeholder="Format Name *"
          value={formatName}
          onChange={(e) => setFormatName(e.target.value)}
        />
        <select className="mt-crf-input mt-crf-select" value={modalityTag} onChange={(e) => setModalityTag(e.target.value)}>
          <option value="" disabled hidden>Select Modality</option>
          {MODALITIES.filter((m) => m !== "ALL").map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <select className="mt-crf-input mt-crf-select" value={bodyPartTag} onChange={(e) => setBodyPartTag(e.target.value)}>
          <option value="" disabled hidden>Select Body Part</option>
          {BODY_PARTS.filter((b) => b !== "ALL").map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
        <select className="mt-crf-input mt-crf-select" value={genderTag} onChange={(e) => setGenderTag(e.target.value)}>
          <option value="" disabled hidden>Select Gender</option>
          {GENDERS.filter((g) => g !== "ALL").map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>
      </div>

      <RichTextEditor editorRef={editorRef} />

      {error && <div className="mt-error-text">{error}</div>}

      <div className="mt-cs-footer">
        <button className="mt-cs-close-btn" onClick={onClose} disabled={submitting}>CANCEL</button>
        <button className="mt-cs-create-btn" onClick={handleCreate} disabled={submitting}>
          {submitting ? "CREATING..." : "CREATE REPORT FORMAT"}
        </button>
      </div>
    </div>
  );
}

function UploadFolderModal({ onClose, onUpload, submitting, error }) {
  const [modality, setModality] = useState("");
  const [files, setFiles] = useState([]);

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files || []));
  };

  const handleSubmit = () => {
    if (!files.length) return;
    onUpload?.({ modality, files });
  };

  return (
    <div className="mt-modal-overlay" onClick={submitting ? undefined : onClose}>
      <div className="mt-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="mt-modal-title">Upload Folder of Docs</h3>

        <select
          className="mt-modal-select"
          value={modality}
          onChange={(e) => setModality(e.target.value)}
        >
          <option value="" disabled>Select Modality tag context</option>
          {MODALITIES.filter((m) => m !== "ALL").map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>

        <div className="mt-modal-file-row">
          <label className="mt-modal-choose-file-btn">
            Choose Folder
            <input type="file" onChange={handleFileChange} webkitdirectory="" directory="" multiple hidden />
          </label>
          <span className="mt-modal-file-name">
            {files.length ? `${files.length} file(s) selected` : "No folder chosen"}
          </span>
        </div>

        {error && <div className="mt-error-text">{error}</div>}

        <div className="mt-modal-actions">
          <button className="mt-modal-close-btn" onClick={onClose} disabled={submitting}>CLOSE</button>
          <button className="mt-modal-submit-btn" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "UPLOADING..." : "SUBMIT FOLDER"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Main Component -----------------------------------------------------
export default function ManageTemplate() {
  const [activeTab, setActiveTab] = useState("ALL");
  const [filters, setFilters] = useState({
    description: "",
    shortcut: "",
    bodyPart: "ALL",
    gender: "ALL",
    modality: "ALL",
    type: "ALL",
  });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [mode, setMode] = useState("list"); 

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const templateType = TAB_TO_TEMPLATE_TYPE[activeTab];
      const qs = templateType ? `?template_type=${encodeURIComponent(templateType)}` : "";
      const data = await apiFetch(`/templates/${qs}`);
      const list = Array.isArray(data) ? data : data.results || [];
      setTemplates(list.map(normalizeTemplate));
    } catch (err) {
      setLoadError(err.message || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const setFilter = (key, val) => {
    setFilters((p) => ({ ...p, [key]: val }));
    setCurrentPage(1);
  };

  const handleEditTrigger = (row) => {
    // UI placeholder hook for subsequent integration of edit views
    console.log("Trigger edit action for structural template reference:", row);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this template?")) return;
    const prev = templates;
    setTemplates((cur) => cur.filter((t) => t.id !== id));
    try {
      await apiFetch(`/templates/${id}/`, { method: "DELETE" });
    } catch (err) {
      setTemplates(prev);
      window.alert(err.message || "Failed to delete template");
    }
  };

  const changeTab = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const filtered = templates.filter((t) => {
    const descOk = t.description.toLowerCase().includes(filters.description.toLowerCase());
    const shortcutOk = t.shortcut.toLowerCase().includes(filters.shortcut.toLowerCase());
    const bodyOk = filters.bodyPart === "ALL" || t.bodyPart === filters.bodyPart;
    const genderOk = filters.gender === "ALL" || t.gender === filters.gender;
    const modalityOk = filters.modality === "ALL" || t.modality === filters.modality;
    const typeOk = filters.type === "ALL" || t.type === filters.type;

    return descOk && shortcutOk && bodyOk && genderOk && modalityOk && typeOk;
  });

  const totalRows = filtered.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage) || 1;
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
  const paginated = filtered.slice(startIndex, endIndex);

  const searchPlaceholder =
    activeTab === "ALL" ? "Search descriptions & settings..." :
    activeTab === "REPORT FORMAT" ? "Search formats..." :
    "Search system shortcuts...";

  const handleCreateShortcut = async ({ shortcut, content }) => {
    setSubmitting(true);
    setSubmitError("");
    try {
      await apiFetch("/shortcut/", {
        method: "POST",
        body: JSON.stringify({ shortcut, content, description: shortcut }),
      });
      setMode("list");
      await fetchTemplates();
    } catch (err) {
      setSubmitError(err.message || "Failed to create shortcut");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateReportFormat = async ({ formatName, modalityTag, bodyPartTag, genderTag, content }) => {
    let doctorId = null;
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      doctorId = user?.id;
    } catch (e) {
      console.error("Failed to parse current session dashboard credentials cleanly:", e);
    }

    setSubmitting(true);
    setSubmitError("");
    try {
      await apiFetch("/report-format/", {
        method: "POST",
        body: JSON.stringify({
          description: formatName,
          modality: modalityTag,
          body_part: bodyPartTag,
          gender: genderTag,
          content,
          doctor_id: doctorId
        }),
      });
      setMode("list");
      await fetchTemplates();
    } catch (err) {
      setSubmitError(err.message || "Failed to create report format");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadTemplate = async ({ modality, files }) => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const formData = new FormData();
      formData.append("modality", modality);
      files.forEach((file) => formData.append("files", file));

      await apiFetch("/upload-template/", {
        method: "POST",
        body: formData,
      });
      setShowUploadModal(false);
      await fetchTemplates();
    } catch (err) {
      setSubmitError(err.message || "Failed to upload template folder");
    } finally {
      setSubmitting(false);
    }
  };

  if (mode === "createShortcut") {
    return (
      <CreateShortcutView
        onClose={() => { setMode("list"); setSubmitError(""); }}
        onCreate={handleCreateShortcut}
        submitting={submitting}
        error={submitError}
      />
    );
  }

  if (mode === "createReportFormat") {
    return (
      <CreateReportFormatView
        onClose={() => { setMode("list"); setSubmitError(""); }}
        onCreate={handleCreateReportFormat}
        submitting={submitting}
        error={submitError}
      />
    );
  }

  return (
    <div className="mt-container">
      {/* ---------------- Unified Global Control Bar ---------------- */}
      <div className="mt-action-bar">
        <div className="mt-action-left">
          {(activeTab === "ALL" || activeTab === "REPORT FORMAT") && (
            <button
              className="mt-btn mt-btn-blue"
              onClick={() => { setSubmitError(""); setMode("createReportFormat"); }}
            >
              <span className="mt-btn-icon">+</span> REPORT FORMAT
            </button>
          )}
          {(activeTab === "ALL" || activeTab === "SHORTCUT") && (
            <button
              className="mt-btn mt-btn-outline"
              onClick={() => { setSubmitError(""); setMode("createShortcut"); }}
            >
              <span className="mt-btn-icon">+</span> SHORTCUT
            </button>
          )}
          <button className="mt-btn mt-btn-pink" onClick={() => { setSubmitError(""); setShowUploadModal(true); }}>
            <span className="mt-btn-icon">↑</span> UPLOAD TEMPLATE FOLDER
          </button>
        </div>
        <div className="mt-search-wrapper">
          <svg className="mt-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input 
            type="text" 
            placeholder={searchPlaceholder} 
            value={filters.description}
            onChange={(e) => setFilter("description", e.target.value)}
            className="mt-search-input" 
          />
        </div>
      </div>

      {/* ---------------- Navigation Tab Panels ---------------- */}
      <div className="mt-tabs">
        {TABS.map((tab) => (
          <button
            key={tab}
            className={`mt-tab ${activeTab === tab ? "mt-tab-active" : ""}`}
            onClick={() => changeTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ---------------- Data Table Filters ---------------- */}
      <div className="mt-filter-row">
        {activeTab !== "SHORTCUT" && (
          <div className="mt-filter-field mt-filter-description">
            <span className="mt-filter-label">Description Filter</span>
            <input
              type="text"
              value={filters.description}
              onChange={(e) => setFilter("description", e.target.value)}
              placeholder="Filter text..."
              className="mt-filter-input mt-filter-input-outlined"
            />
          </div>
        )}

        {activeTab === "ALL" && (
          <div className="mt-filter-field">
            <span className="mt-filter-label">Shortcut</span>
            <input
              type="text"
              value={filters.shortcut}
              onChange={(e) => setFilter("shortcut", e.target.value)}
              placeholder="Filter code..."
              className="mt-filter-input mt-filter-input-plain"
            />
          </div>
        )}

        {activeTab === "SHORTCUT" && (
          <>
            <div className="mt-filter-field mt-filter-description">
              <span className="mt-filter-label">Shortcut Code</span>
              <input
                type="text"
                value={filters.shortcut}
                onChange={(e) => setFilter("shortcut", e.target.value)}
                placeholder="Search shortcut..."
                className="mt-filter-input mt-filter-input-outlined"
              />
            </div>
            <div className="mt-filter-content-preview-label">Preview Text Snippet</div>
          </>
        )}

        {activeTab !== "SHORTCUT" && (
          <>
            <SelectField label="Body Part" value={filters.bodyPart} options={BODY_PARTS} onChange={(v) => setFilter("bodyPart", v)} />
            <SelectField label="Gender" value={filters.gender} options={GENDERS} onChange={(v) => setFilter("gender", v)} />
            <SelectField label="Modality" value={filters.modality} options={MODALITIES} onChange={(v) => setFilter("modality", v)} />
          </>
        )}

        <SelectField label="Scope Type" value={filters.type} options={TYPES} onChange={(v) => setFilter("type", v)} />
        <div className="mt-filter-actions-label">Actions</div>
      </div>

      {/* ---------------- State Information Overlays ---------------- */}
      {loading && <div className="mt-loading-text">Loading layout configurations...</div>}
      {!loading && loadError && (
        <div className="mt-error-text">
          {loadError}{" "}
          <button className="mt-retry-btn" onClick={fetchTemplates}>Retry Connection</button>
        </div>
      )}

      {/* ---------------- Interactive List Table Grid ---------------- */}
      {!loading && !loadError && (
        <div className="mt-table-body">
          {paginated.map((row) => (
            <div key={row.id} className="mt-row">
              {activeTab === "ALL" && (
                <>
                  <div className="mt-cell mt-cell-description">{row.description}</div>
                  <div className="mt-cell mt-cell-shortcut">{row.shortcut || "—"}</div>
                </>
              )}

              {activeTab === "REPORT FORMAT" && (
                <div className="mt-cell mt-cell-description mt-cell-wide">{row.description}</div>
              )}

              {activeTab === "SHORTCUT" && (
                <>
                  <div className="mt-cell mt-cell-shortcut">{row.shortcut}</div>
                  <div className="mt-cell mt-cell-content-preview">{row.contentPreview}</div>
                </>
              )}

              <div className="mt-cell mt-cell-type">
                <TypeBadge type={row.type} />
              </div>

              <RowActions 
                onEdit={() => handleEditTrigger(row)} 
                onDelete={() => handleDelete(row.id)} 
              />
            </div>
          ))}

          {paginated.length === 0 && (
            <div className="mt-empty-text">No template definitions match current parameters.</div>
          )}
        </div>
      )}

      {/* ---------------- Layout Table Pagination Controls ---------------- */}
      <div className="mt-footer">
        <div className="mt-footer-left">
          {/* Action features or contextual data details can be safely mapped here */}
        </div>
        <div className="mt-footer-right">
          <span className="mt-rows-label">Rows per view:</span>
          <select
            className="mt-rows-select"
            value={rowsPerPage}
            onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="mt-page-info">
            {totalRows === 0 ? 0 : startIndex + 1}–{endIndex} of {totalRows}
          </span>
          <div className="mt-page-btns">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="mt-page-btn"
            >‹</button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="mt-page-btn"
            >›</button>
          </div>
        </div>
      </div>

      {showUploadModal && (
        <UploadFolderModal
          onClose={() => { setShowUploadModal(false); setSubmitError(""); }}
          onUpload={handleUploadTemplate}
          submitting={submitting}
          error={submitError}
        />
      )}
    </div>
  );
}